import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Tooltip, TooltipProvider } from '../../shared/ui';
import { CmsContext } from '../../context/definitions';
import { homeItem, iconFor, railMenu, resolveLabel, toGroups } from '../definitions';
import { useRailScroll } from '../railScroll';
import { useSelectedItem } from '../useSelectedItem';

/**
 * The CMS app rail: logo corner and grouped navigation.
 *
 * SURF-4808 step 1b. Replaces `modules/sidebar/sidebar.html` and its controller. The
 * Angular version could not be tested, and it collapsed a group by reaching into the
 * document and toggling a class on every element whose `class` attribute matched the
 * group's label, which worked for a two-word group like "User Management" only because
 * `getElementsByClassName` treats the space as a separator and both halves happened to
 * be unique.
 *
 * Most of what the rail needs is already in the CMS context, so it is read from there
 * rather than passed in: the menu the server built and the message catalogue its labels
 * are keyed against. The frame (`router/components/AppFrame.js`) passes the rest: the
 * current route, navigation, whether the rail is collapsed and the tenant's uploaded logo.
 *
 * The admin's own account is not here. Change Password and Log Out are in the account menu
 * at the right of the top bar, and so are the tenant and subtenant pickers that used to sit
 * under the logo (`topbar/components/AppTopBar.js`); `railMenu` leaves the account items out.
 *
 * Geometry and colour stay in `css/simple-sidebar.css`, addressed by class name. That is
 * deliberate rather than lazy: the frame is one tokenised stylesheet, and a component
 * writing its own pixels would put half the rail outside the token set. It is also what
 * lets this file carry no style object at all, bar the tenant logo's URL, which is data.
 *
 * Collapsed (the button at the start of the top bar), the corner shows the Combobulate mark
 * or the tenant's logo made small, every group is open because there is no caption to click,
 * and each item's name is its tooltip.
 */
const NavItem = ({ item, label, selected, collapsed, onNavigate, onPreload }) => {
  const Icon = iconFor(item);
  // A button, not an anchor. It was `<a role="link" tabIndex={0}>` with a hand-written
  // Enter/Space handler, which is what an anchor with no `href` needs to behave like a
  // control. It also left a focus ring on the last item clicked: Chrome applies
  // `:focus-visible` on a mouse click to an element that is only focusable because of
  // `tabIndex`. A button is natively clickable, so the ring is keyboard-only and Enter
  // and Space come free. There is no href to give: the frame's `navigateToMenuItem` decides
  // what a click does (a route, a named action such as Export Content, or a full page load).
  const button = (
    <button
      type="button"
      className={selected ? 'nav-item selected' : 'nav-item'}
      aria-current={selected ? 'page' : undefined}
      // Collapsed, the label is not drawn, so the button carries its name itself.
      aria-label={collapsed ? label : undefined}
      onClick={() => onNavigate(item)}
      // The page's code starts downloading while the pointer is over the item or the keyboard
      // is on it, so the click usually finds it there (`AppFrame`'s `preloadMenuItem`).
      onPointerEnter={() => onPreload?.(item)}
      onFocus={() => onPreload?.(item)}
    >
      <Icon className="nav-icon" size={17} aria-hidden="true" />
      <span className="nav-label">{label}</span>
    </button>
  );
  // Collapsed, the icon is all there is, so the name has to stay findable. A `title` would
  // show it to a mouse only; the Radix tooltip shows on keyboard focus too. Expanded, the
  // label is right there and a tooltip would be noise.
  return collapsed ? (
    <Tooltip title={label} side="right">
      {button}
    </Tooltip>
  ) : (
    button
  );
};

const NavGroup = ({ group, open, collapsed, onToggle, selected, label, onNavigate, onPreload }) => (
  <div className="nav-group">
    {/* Drawn on the collapsed rail too, where the stylesheet folds it away, so the items
        glide up as the rail narrows rather than jumping. Folded, it is out of the tab order
        and hidden from assistive tech, since there is nothing on screen to reach. */}
    {group.label ? (
      <button
        type="button"
        className="nav-group-label"
        onClick={onToggle}
        aria-expanded={open}
        aria-hidden={collapsed ? 'true' : undefined}
        tabIndex={collapsed ? -1 : undefined}
        title={label(group.label)}
      >
        {/* No icon. `group.icon` is still in the tenant's menu config and is deliberately
            not rendered: an icon in the header pushed its label out to the same x as the
            item labels below it, so the two levels read as one flat list. The header is
            a caption now and the items indent from it. */}
        <span className="nav-group-title">{label(group.label)}</span>
        <ChevronDown className={open ? 'caretRight' : 'caretRight spin'} size={14} />
      </button>
    ) : null}
    {open && (
      <ul className="nav-group-items">
        {group.items.map((item) => (
          <li key={item.text}>
            <NavItem
              item={item}
              label={label(item.text)}
              selected={item === selected}
              collapsed={collapsed}
              onNavigate={onNavigate}
              onPreload={onPreload}
            />
          </li>
        ))}
      </ul>
    )}
  </div>
);

const AppSidebar = ({ route, logoUrl, collapsed = false, onNavigate, onPreload }) => {
  const { cmsConfig } = useContext(CmsContext);
  // Closed groups, by label. Every group starts open, as before.
  const [closed, setClosed] = useState({});

  const serverMenu = cmsConfig?.sideMenu;
  const menu = useMemo(() => railMenu(serverMenu), [serverMenu]);
  const messages = cmsConfig?.messages;
  const label = useCallback((text) => resolveLabel(messages, text), [messages]);

  const groups = useMemo(() => toGroups(menu), [menu]);
  // The item the route belongs to; the top bar's breadcrumbs ask the same hook.
  const selected = useSelectedItem(menu, route);
  // The rail's own scroll position, kept across full page loads, and the highlighted item
  // kept in view. SURF-4829; see `../railScroll`.
  const navRef = useRef(null);
  useRailScroll(navRef, selected);
  // The logo goes to the navbar, which is the CMS's home screen. It navigates through the
  // menu item rather than a hardcoded state, so it lands wherever this tenant's navbar
  // actually is, and renders as plain artwork on a tenant that has no navbar plugin.
  const home = useMemo(() => homeItem(menu), [menu]);

  const toggleGroup = useCallback(
    (groupLabel) => setClosed((previous) => ({ ...previous, [groupLabel]: !previous[groupLabel] })),
    []
  );

  // Collapsed, the shipped logo becomes the Combobulate mark and a tenant's own logo is drawn
  // small in the middle of the corner; both are in simple-sidebar.css.
  const logo = (
    <span
      className={logoUrl ? 'cms-banner-logo' : 'cms-banner-img'}
      style={logoUrl ? { backgroundImage: `url(${logoUrl})` } : undefined}
      role="img"
      aria-label="Combobulate"
    />
  );

  return (
    <aside className="app-rail" data-collapsed={collapsed ? '' : undefined}>
      <div className="app-logo">
        {home ? (
          <button
            type="button"
            className="app-logo-link"
            onClick={() => onNavigate(home)}
            onPointerEnter={() => onPreload?.(home)}
            onFocus={() => onPreload?.(home)}
            aria-label="Go to the NavBar"
          >
            {logo}
          </button>
        ) : (
          logo
        )}
      </div>

      {/* Its own provider, as well as the app's (`store/rootComponents/Theme.js`), which Radix
          allows: collapsed, the rail's names are only in tooltips, so it must not throw if it
          is ever rendered outside that tree. */}
      <TooltipProvider delayDuration={0}>
        <nav id="sidebar-wrapper" className="sidebar" aria-label="Main" ref={navRef}>
          {groups.map((group) => (
            <NavGroup
              key={group.label || 'ungrouped'}
              group={group}
              // A collapsed rail draws no captions, so there is nothing to click to open a
              // group. Leaving one closed would strand its items behind a caption not on screen.
              open={collapsed || !closed[group.label]}
              collapsed={collapsed}
              onToggle={() => toggleGroup(group.label)}
              selected={selected}
              label={label}
              onNavigate={onNavigate}
              onPreload={onPreload}
            />
          ))}
        </nav>
      </TooltipProvider>
    </aside>
  );
};

export default AppSidebar;
